import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, Button, TextField, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import { BASE_URL } from '../../utils/endpoints';

const ProductEdit = ({ open, handleClose, product, token, onProductUpdated }) => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        product_title: '',
        product_description: '',
        product_price: '0',
        product_slider_images: '',
        category: '',
        interests: '',
        search_keywords: '',
        status: '',
        main_image: '',
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Fetch categories from API
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch('https://stage.socioglamm.com/categories');
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const data = await response.json();
                ('Categories API Response:', data);

                const categoriesArray = Array.isArray(data) ? data : data.categories || [];
                setCategories(categoriesArray);
            } catch (err) {
                setError('Error fetching categories: ' + err.message);
                setCategories([]);
            }
        };
        fetchCategories();
    }, []);

    // Populate form data when product changes
    useEffect(() => {
        if (product) {
            setFormData({
                product_title: product.name || '',
                product_description: product.product_description || '',
                product_price: product.price || "0",
                product_slider_images: (product.product_slider_images || []).join(', '),
                category: product.category || '',
                interests: (product.interests || []).join(', '),
                search_keywords: (product.search_keywords || []).join(', '),
                status: product.status === 'Approved' ? 'approved' : 'pending',
                main_image: product.image || '',
                original_price: product.orgprice || '',
            });
            setError(null);
            setSuccessMessage('');
        }
    }, [product]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage('');

        const putData = {
            ...formData,
            product_slider_images: formData.product_slider_images.split(',').map((item) => item.trim()).filter(Boolean),
            categories: [formData.category],
            interests: formData.interests.split(',').map((item) => item.trim()).filter(Boolean),
            search_keywords: formData.search_keywords.split(',').map((item) => item.trim()).filter(Boolean),
            product_price: formData.product_price,
            original_price: formData.original_price,
        };

        try {
            const response = await fetch(`${BASE_URL}/products/${product.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(putData),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`HTTP error! Status: ${response.status} - ${errorData.message || response.statusText}`);
            }

            const data = await response.json();
            ('Product Updated:', data);
            setSuccessMessage('Product updated successfully!');
            if (onProductUpdated) {
                onProductUpdated();
            }
            setTimeout(() => {
                handleClose();
                // navigate('/getproduct');
            }, 1000);
        } catch (err) {
            console.error('Update Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!product) {
        return null;
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="product-modal-title"
            aria-describedby="product-modal-description"
        >
            <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 650,
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Typography id="product-modal-title" variant="h6" component="h2" sx={{ color: '#2e7d32' }}>
                    <strong>Edit Product:</strong> {product.name || 'Unnamed Product'}
                    <EditIcon sx={{ verticalAlign: 'middle', color: '#2e7d32', ml: 1 }} />
                </Typography>

                <Box
                    component="img"
                    src={formData.main_image || `https://via.placeholder.com/150?text=${formData.product_title?.[0] || 'N'}`}
                    sx={{ width: '100%', height: 'auto', objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                    alt="Product Image"
                    onClick={() => window.open(formData.main_image, '_blank')}
                />

                <TextField
                    label="Product Title"
                    name="product_title"
                    value={formData.product_title}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                    }}
                />

                <TextField
                    label="Product Description"
                    name="product_description"
                    value={formData.product_description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    size="small"
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                    }}
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label="Offer Price"
                        name="product_price"
                        value={formData.product_price}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ style: { color: '#2e7d32' } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                        // InputProps={{
                        //     startAdornment: <span style={{ marginRight: 4, color: '#2e7d32' }}>₹</span>,
                        // }}
                    />
                    <TextField
                        label="Original Price"
                        name="original_price"
                        value={formData.original_price}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ style: { color: '#2e7d32' } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                        // InputProps={{
                        //     startAdornment: <span style={{ marginRight: 4, color: '#2e7d32' }}>₹</span>,
                        // }}
                    />
                    <TextField
                        select
                        label="Status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        SelectProps={{ native: true }}
                        InputLabelProps={{ style: { color: '#2e7d32' } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                    >
                        <option value="approved">Approved</option>
                        <option value="pending">Pending</option>
                    </TextField>
                </Box>

                <TextField
                    label="Main Image URL"
                    name="main_image"
                    value={formData.main_image}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                        mb: 1,
                    }}
                    InputProps={{
                        endAdornment: (
                            <Button
                                size="small"
                                sx={{ color: '#2e7d32', textTransform: 'none' }}
                                onClick={() => window.open(formData.main_image, '_blank')}
                                disabled={!formData.main_image}
                            >
                                Preview
                            </Button>
                        ),
                    }}
                />

                <TextField
                    label="Product Slider Images (comma-separated URLs)"
                    name="product_slider_images"
                    value={formData.product_slider_images}
                    onChange={handleChange}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                    InputLabelProps={{ style: { color: '#2e7d32' } }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#2e7d32' },
                            '&:hover fieldset': { borderColor: '#2e7d32' },
                            '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                        },
                        mb: 1,
                    }}
                    placeholder="https://img1.jpg, https://img2.jpg"
                />

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                        <FormControl
                            fullWidth
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#2e7d32' },
                                    '&:hover fieldset': { borderColor: '#2e7d32' },
                                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                },
                            }}
                        >
                            <InputLabel style={{ color: '#2e7d32' }}>Category</InputLabel>
                            <Select
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                label="Category"
                                sx={{ color: '#2e7d32' }}
                            >
                                <MenuItem value="" disabled>
                                    Select a category
                                </MenuItem>
                                {Array.isArray(categories) && categories.length > 0 ? (
                                    categories.map((cat) => (
                                        <MenuItem key={cat.id} value={cat.name}>
                                            {cat.name}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>
                                        No categories available
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formData.category && (
                                <Box sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                    {formData.category}
                                </Box>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <TextField
                            label="Interests"
                            name="interests"
                            value={formData.interests}
                            on encouragement={handleChange}
                            fullWidth
                            size="small"
                            InputLabelProps={{ style: { color: '#2e7d32' } }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: '#2e7d32' },
                                    '&:hover fieldset': { borderColor: '#2e7d32' },
                                    '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                },
                            }}
                            placeholder="Comma-separated"
                        />
                        <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(formData.interests.split(',').map((item) => item.trim()).filter(Boolean)).map((interest, idx) => (
                                <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                    {interest}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>

                <Box>
                    <TextField
                        label="Search Keywords"
                        name="search_keywords"
                        value={formData.search_keywords}
                        onChange={handleChange}
                        fullWidth
                        size="small"
                        InputLabelProps={{ style: { color: '#2e7d32' } }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#2e7d32' },
                                '&:hover fieldset': { borderColor: '#2e7d32' },
                                '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                            },
                        }}
                        placeholder="Comma-separated"
                    />
                    <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(formData.search_keywords.split(',').map((item) => item.trim()).filter(Boolean)).map((keyword, idx) => (
                            <Box key={idx} sx={{ px: 1, py: 0.2, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 1, fontSize: 13 }}>
                                {keyword}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {loading && <CircularProgress size={24} sx={{ alignSelf: 'center' }} />}
                {error && <Alert severity="error">{error || "Not Updated"}</Alert>}
                {successMessage && <Alert severity="success">{successMessage}</Alert>}

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            borderColor: '#2e7d32',
                            borderWidth: 1,
                            color: '#2e7d32',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        sx={{
                            backgroundColor: '#2e7d32',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#45a049',
                            },
                        }}
                        disabled={loading}
                        лиш>
                        Save Changes
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default ProductEdit;